import React, { useState, useEffect } from 'react';
import { Card, Typography, Box, styled, IconButton, Grid, Dialog, DialogTitle, DialogActions } from '@mui/material';
import ImageUploadPopup from './ImageUploadPopup';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

const StyledImg = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain', // שומר על יחסי התמונה ומוסיף רווחים אם צריך
  backgroundColor: '#f0f0f0', // צבע רקע עבור רווחים
});

function ImageGraveDetiles({ columns, image, imageName }) {
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false); // עבור פתיחת הפופאפ של התמונה
  const [refreshedImageUrl, setRefreshedImageUrl] = useState(image);
  const [zoom, setZoom] = useState(1); // מצב הזום של התמונה

  const handleOpenDialog = () => {
    setOpenUploadDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenUploadDialog(false);
  };

  const handleImageClick = () => {
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setZoom(1); // איפוס הזום בעת סגירה
  };

  const handleZoomIn = () => {
    setZoom((prevZoom) => Math.min(prevZoom + 0.2, 5)); // מקסימום זום פי 5
  };

  const handleZoomOut = () => {
    setZoom((prevZoom) => Math.max(prevZoom - 0.2, 1)); // מינימום זום פי 1
  };

  useEffect(() => {
   const intervalId = setInterval(async () => {
       try {
           // החלפת רווחים בכתובת (אם קיימים)
           const sanitizedImage = image.replace(/ /g, '%20');
           const newImageUrl = `${sanitizedImage}?t=${Date.now()}`;

           // בדיקת תמונה עם Promise
           const checkImage = (url) => {
               return new Promise((resolve, reject) => {
                   const img = new Image();

                   img.onload = () => resolve(true); // תמונה נטענה בהצלחה
                   img.onerror = () => reject(new Error(`Error loading the image: ${url}`)); // שגיאה בטעינה

                   img.src = url; // קביעת מקור התמונה
               });
           };

           // בדיקת אם התמונה החדשה שונה
           const isImageDifferent = await checkImage(newImageUrl);

           if (isImageDifferent && refreshedImageUrl !== newImageUrl) {
               setRefreshedImageUrl(newImageUrl); // עדכון ה-URL רק אם התמונה שונה
           } else {
               console.log('Image is the same, no update needed');
           }
       } catch (error) {
           console.error('Error checking image URL:', error.message);
       }
   }, 5000); // הפעלת הפונקציה כל 5 שניות

   return () => clearInterval(intervalId); // ניקוי ה-interval ביציאה
}, [image, refreshedImageUrl]);

  return (
    <Grid container spacing={2} style={{ margin: 0, marginTop: '20px', width: '100%' }}>
      <Card sx={{ width: '100%', paddingTop: 2, paddingBottom: 2 }}>
        <Grid container spacing={2} columns={16}>
          <Grid item xs={8} sx={{ textAlign: 'left', width: '100%' }}>
            <Grid item xs={12} sx={{ width: '100%', textAlign: 'left' }}>
              <Box sx={{ width: '100%', textAlign: 'left', paddingLeft: 10 }}>
                {columns?.map((row, index) => (
                  index > 0 && (
                    <Typography
                      key={index}
                      color="textSecondary"
                      sx={{ width: '100%', marginTop: 1.6, display: 'block' }}
                    >
                      <span style={{ fontWeight: 'bold' }}>{row?.title}</span>
                      <span>{': '}</span>
                      <span>{row?.value}</span>
                    </Typography>
                  )
                ))}
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={8} sx={{ textAlign: 'left', width: '100%', position: 'relative' }}>
            <Card sx={{ width: 240, height: '100%', position: 'relative' }}>
              <StyledImg
                src={refreshedImageUrl}
                alt="Description of Image"
                onClick={handleImageClick} // פתיחת הפופאפ בעת לחיצה על התמונה
              />
              <IconButton
                onClick={handleOpenDialog}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                }}
              >
                <PhotoCameraIcon />
              </IconButton>
            </Card>
          </Grid>
        </Grid>
      </Card>

      <ImageUploadPopup
        open={openUploadDialog}
        imageNameWithTime={refreshedImageUrl}
        imageName={imageName}
        onClose={handleCloseDialog}
      />

      {/* פופאפ לצפייה בתמונה בהגדלה */}
      <Dialog open={openImageDialog} onClose={handleCloseImageDialog} maxWidth="xl">
        <DialogTitle>
          צפייה בתמונה
          <IconButton
            onClick={handleCloseImageDialog}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'auto',
            maxHeight: '90vh',
            maxWidth: '90vw',
          }}
        >
          <img
            src={refreshedImageUrl}
            alt="Zoomed Image"
            style={{
              transform: `scale(${zoom})`,
              transition: 'transform 0.3s',
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          />
        </Box>
        <DialogActions>
          <IconButton onClick={handleZoomIn}>
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={handleZoomOut}>
            <ZoomOutIcon />
          </IconButton>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default ImageGraveDetiles;
